import axios from "axios";
import { API } from "./api";

const ADMIN_TOKEN_KEY = "ups_hub_admin_token";
const ADMIN_USER_KEY = "ups_hub_admin_user";

export const getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY);
export const saveAdminToken = (t) => localStorage.setItem(ADMIN_TOKEN_KEY, t);
export const clearAdminToken = () => {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_USER_KEY);
};

export const getAdminUser = () => {
  try { return JSON.parse(localStorage.getItem(ADMIN_USER_KEY) || "null"); }
  catch { return null; }
};
export const saveAdminUser = (u) => localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(u));

/**
 * Build an axios client with the Bearer JWT attached. Also continues to
 * forward the token as X-Admin-Password so any older code paths that still
 * read that header keep working without churn.
 */
const adminClient = () => {
  const token = getAdminToken() || "";
  return axios.create({
    baseURL: API,
    headers: {
      "Authorization": token ? `Bearer ${token}` : "",
      "X-Admin-Password": token,
    },
  });
};

export { adminClient };

/* -------- Auth -------- */
export const adminLogin = async (username, password) => {
  const res = await axios.post(`${API}/auth/admin-login`, { username, password });
  return res.data;
};

export const authMe = async () => (await adminClient().get("/auth/me")).data;
export const authLogout = async () => {
  try { await adminClient().post("/auth/logout"); } catch { /* ignore */ }
  clearAdminToken();
};

/* -------- User management (owner only) -------- */
export const listAdminUsers = async () => (await adminClient().get("/admin/users")).data.items || [];
export const createAdminUser = async (payload) => (await adminClient().post("/admin/users", payload)).data;
export const updateAdminUser = async (id, patch) => (await adminClient().patch(`/admin/users/${id}`, patch)).data;
export const deleteAdminUser = async (id) => (await adminClient().delete(`/admin/users/${id}`)).data;

export const adminGetStats = async () => (await adminClient().get("/admin/stats")).data;
export const adminListSessions = async () => (await adminClient().get("/admin/sessions")).data;
export const adminGetSession = async (id) =>
  (await adminClient().get(`/admin/sessions/${id}`)).data;
export const adminListOrders = async () => (await adminClient().get("/admin/orders")).data;

// Products
export const adminListProducts = async () => (await adminClient().get("/products")).data;
export const adminCreateProduct = async (p) =>
  (await adminClient().post("/products", p)).data;
export const adminUpdateProduct = async (id, p) =>
  (await adminClient().patch(`/products/${id}`, p)).data;
export const adminDeleteProduct = async (id) =>
  (await adminClient().delete(`/products/${id}`)).data;

// Libraries
export const adminListSizes = async () => (await adminClient().get("/sizes")).data;
export const adminCreateSize = async (s) => (await adminClient().post("/sizes", s)).data;
export const adminUpdateSize = async (id, s) => (await adminClient().patch(`/sizes/${id}`, s)).data;
export const adminDeleteSize = async (id) => (await adminClient().delete(`/sizes/${id}`)).data;

export const adminListPapers = async () => (await adminClient().get("/papers")).data;
export const adminCreatePaper = async (p) => (await adminClient().post("/papers", p)).data;
export const adminUpdatePaper = async (id, p) => (await adminClient().patch(`/papers/${id}`, p)).data;
export const adminDeletePaper = async (id) => (await adminClient().delete(`/papers/${id}`)).data;

export const adminListFinishes = async () => (await adminClient().get("/finishes")).data;
export const adminCreateFinish = async (f) => (await adminClient().post("/finishes", f)).data;
export const adminUpdateFinish = async (id, f) => (await adminClient().patch(`/finishes/${id}`, f)).data;
export const adminDeleteFinish = async (id) => (await adminClient().delete(`/finishes/${id}`)).data;

export const adminReseedCatalog = async () =>
  (await adminClient().post("/products/seed?force=true")).data;

export const adminGetPricingMatrix = async (productId) =>
  (await adminClient().get(`/admin/pricing-matrix/${productId}`)).data;

export const adminListDomainPolicies = async () =>
  (await adminClient().get(`/admin/domain-policies`)).data;
export const adminUpsertDomainPolicy = async (p) =>
  (await adminClient().post(`/admin/domain-policies`, p)).data;
export const adminDeleteDomainPolicy = async (domain) =>
  (await adminClient().delete(`/admin/domain-policies/${encodeURIComponent(domain)}`)).data;
export const adminListAccessRequests = async () =>
  (await adminClient().get(`/admin/access-requests`)).data;
export const adminUpdateAccessRequest = async (id, status) =>
  (await adminClient().patch(`/admin/access-requests/${encodeURIComponent(id)}?status=${encodeURIComponent(status)}`)).data;
